/**
 * React Query hooks for user profile management
 * Provides type-safe data fetching and mutations for user profile
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { api } from '@/services/api';
import type { UserProfile, UpdateProfileRequest } from '@/types';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch the current user's profile
 */
export function useProfile(): UseQueryResult<UserProfile, Error> {
  return useQuery({
    queryKey: profileKeys.me(),
    queryFn: async () => {
      const profile = await api.get<UserProfile>('/users/me');
      return profile;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Update the current user's profile
 */
export function useUpdateProfile(): UseMutationResult<
  UserProfile,
  Error,
  UpdateProfileRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProfileRequest) => {
      const profile = await api.put<UserProfile>('/users/me', data);
      return profile;
    },
    onSuccess: (updatedProfile) => {
      // Update the profile in the cache
      queryClient.setQueryData(profileKeys.me(), updatedProfile);
    },
    onError: (error) => {
      console.error('Failed to update profile:', error);
    },
  });
}
